import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { formatNumber } from "src/helpers/Dashboard";
import WidgetPlaceholder from "./WidgetPlaceholder";
import DataTableModal from "../modals/DataTableModal";
import KpiComparison, { type KpiComparisonData } from "./KpiComparison";
import {
  exportDashboardWidgetDataApi,
  getDashboardWidgetDataApi,
  type DashboardWidgetExportFormat,
} from "src/api/dashboardApi";
import {
  downloadExportFile,
  type ReportExportResponse,
} from "src/helpers/Report";

type KpiPagination = {
  page?: number;
  page_size?: number;
  total?: number;
  total_pages?: number;
};

type KpiWithDetailData = {
  total_count?: number;
  count?: number;
  comparison?: KpiComparisonData | null;
  data?: unknown[];
  pagination?: KpiPagination;
};

type KpiWithDetailWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

export default function KpiWithDetailWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: KpiWithDetailWidgetProps) {
  const [isModalTableOpen, setModalTableOpen] = useState<boolean>(false);

  const isDataUnavailable = data === undefined || data === null;
  const kpiDetailedData = (data as KpiWithDetailData | null) ?? {};
  const displayValue =
    kpiDetailedData.total_count ?? kpiDetailedData.count ?? 0;
  const initialPage = kpiDetailedData.pagination?.page ?? 1;
  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  useEffect(() => {
    if (!isModalTableOpen) {
      setCurrentPage(initialPage);
    }
  }, [initialPage, isModalTableOpen]);

  const shouldFetchModalPage =
    isModalTableOpen &&
    !isDataUnavailable &&
    !!dashboardName &&
    widgetTemplateId !== undefined &&
    widgetTemplateId !== null &&
    currentPage !== initialPage;

  const canDownloadWidgetData =
    !!dashboardName &&
    widgetTemplateId !== undefined &&
    widgetTemplateId !== null;

  const {
    data: modalPageResponse,
    isLoading: isModalPageLoading,
    isFetching: isModalPageFetching,
  } = useQuery({
    queryKey: [
      "kpi_with_detail_page",
      dashboardName,
      widgetTemplateId,
      currentPage,
      appliedFilters,
    ],
    queryFn: async ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName as string,
        String(widgetTemplateId),
        {
          filters: appliedFilters ?? {},
          page: currentPage,
        },
        { signal },
      ),
    enabled: shouldFetchModalPage,
    staleTime: 30_000,
  });

  const { mutate: downloadWidgetData, isPending: isWidgetDataDownloading } =
    useMutation<ReportExportResponse, Error, DashboardWidgetExportFormat>({
      mutationFn: (format) => {
        if (
          !dashboardName ||
          widgetTemplateId === undefined ||
          widgetTemplateId === null
        ) {
          throw new Error("Missing widget context for download.");
        }

        return exportDashboardWidgetDataApi(
          dashboardName,
          String(widgetTemplateId),
          {
            filters: appliedFilters ?? {},
          },
          format,
        );
      },
      onSuccess: async (response, format) => {
        const normalizedTitle =
          title
            .trim()
            .replace(/[\\/:*?"<>|]+/g, "_")
            .replace(/\s+/g, "_") || "widget";

        try {
          await downloadExportFile({
            ...response,
            filename: `${normalizedTitle}.${format}`,
          });
        } catch {
          toast.error("Failed to process download file.");
        }
      },
      onError: (error) => {
        toast.error(error.message || "Failed to download widget data.");
      },
    });

  const resolvedModalData = (() => {
    if (isDataUnavailable) {
      return {} as KpiWithDetailData;
    }

    if (!shouldFetchModalPage) {
      return kpiDetailedData;
    }

    const responseData = (modalPageResponse as { data?: unknown } | undefined)
      ?.data;

    if (!responseData || typeof responseData !== "object") {
      return kpiDetailedData;
    }

    return responseData as KpiWithDetailData;
  })();

  const resolvedPagination = resolvedModalData.pagination;
  const resolvedTableData = resolvedModalData.data;

  if (isLoading || isDataUnavailable) {
    return <WidgetPlaceholder height={48} />;
  }

  return (
    <>
      <div className="kpi-counter-widget d-flex flex-column flex-fill align-items-center justify-content-between text-center">
        <button
          className="kpi-counter-widget__value-link btn btn-link p-0 mb-2"
          onClick={() => setModalTableOpen(true)}
        >
          {formatNumber(displayValue)}
        </button>
        <KpiComparison comparison={kpiDetailedData.comparison} />
      </div>
      <DataTableModal
        title={title}
        tableData={resolvedTableData}
        pagination={resolvedPagination}
        isLoading={isModalPageLoading || isModalPageFetching}
        isDownloading={canDownloadWidgetData ? isWidgetDataDownloading : false}
        onDownload={
          canDownloadWidgetData
            ? (format) => downloadWidgetData(format)
            : undefined
        }
        onPageChange={(page) => setCurrentPage(page)}
        show={isModalTableOpen}
        handleClose={() => setModalTableOpen(false)}
      />
    </>
  );
}
